import { QdsToastManager, ToastType } from '../../types/index.js';
import { loadBaseCSS } from '../shared/button-utils.js';

declare global {
  interface Window {
    QdsToastManager?: QdsToastManager;
  }
}

// Global toast state
let globalToastInitialized = false;
let globalToastInitializing = false;
let globalToastInitPromise: Promise<boolean> | null = null;
const DEFAULT_COLOR = 'var(--theme-accent-primary, var(--brand-middle-blue))';

/**
 * @element qds-color-helper
 *
 * A component for displaying color swatches with information and copy functionality.
 * Helps designers and developers use colors consistently by providing easy access
 * to color values in different formats.
 *
 * @attr {string} color - The color to display (hex, rgb, hsl, or CSS variable)
 * @attr {string} variable-name - The CSS variable name (optional)
 * @attr {string} name - Display name for the color
 * @attr {string} description - Description of when to use the color
 *
 * @csspart container - The container element
 * @csspart swatch - The color swatch element
 * @csspart info - The information section element
 * @csspart toolbar - The toolbar with copy buttons
 *
 * @fires copied - When a color value is copied to clipboard
 */

// Load base CSS
loadBaseCSS();

class QdsColorHelper extends HTMLElement {
  // Private properties
  private _color: string;
  private _variableName: string;
  private _name: string;
  private _description: string;
  private _hexClickHandler!: (event: MouseEvent) => void;
  private _rgbClickHandler!: (event: MouseEvent) => void;
  private _varClickHandler!: (event: MouseEvent) => void;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Initialize default property values
    this._color = DEFAULT_COLOR;
    this._variableName = '';
    this._name = 'Color';
    this._description = 'Color description';
    
    // Setup the shadow DOM structure
    this.render();
    
    // Bind methods
    this._copyHex = this._copyHex.bind(this);
    this._copyRgb = this._copyRgb.bind(this);
    this._copyCssVar = this._copyCssVar.bind(this);
    
    // Ensure toast is initialized without waiting (will happen in background)
    if (!globalToastInitialized && !globalToastInitializing) {
      this._initializeGlobalToast();
    }
  }
  
  /**
   * Initialize a global toast component that all color helpers can use
   * @private
   */
  private _initializeGlobalToast(): Promise<boolean> {
    // Prevent multiple simultaneous initialization attempts
    if (globalToastInitializing) return globalToastInitPromise!;
    
    globalToastInitializing = true;
    
    // Create a promise for the initialization
    globalToastInitPromise = (async () => {
      try {
        // First try to load the toast component with various paths
        const paths = [
          // Try absolute paths first (most reliable)
          '/dev/assets/components/toast/qds-toast.js',
          '/dev/assets/components/toast/qds-toast.js',
          // Try relative paths as fallbacks
          '../toast/qds-toast.js',
          '../../components/toast/qds-toast.js',
          '../../../assets/components/toast/qds-toast.js',
          '../../../components/toast/qds-toast.js'
        ];
        
        // Try each path until one works
        let loaded = false;
        for (const path of paths) {
          try {
            await import(path);
            loaded = true;
            break;
          } catch (importErr) {
            // Silently continue to next path
          }
        }
        
        // If none of the paths worked, implement our own toast
        if (!loaded) {
          // Create our own toast element definition
          // This is a self-contained minimal implementation for fallback
          if (!customElements.get('qds-simple-toast')) {
            class SimpleToast extends HTMLElement {
              private _message: string;
              private _type: string;
              private _duration: number;
              private _visible: boolean;
              private _style: HTMLStyleElement;
              private _container: HTMLDivElement;
              
              constructor() {
                super();
                this._message = '';
                this._type = 'info';
                this._duration = 3000;
                this._visible = false;
                
                this._style = document.createElement('style');
                this._container = document.createElement('div');
                this.appendChild(this._style);
                this.appendChild(this._container);
                
                this._style.textContent = `
                  :host {
                    position: fixed;
                    z-index: 9999;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    pointer-events: none;
                  }
                  .toast {
                    position: fixed;
                    top: var(--theme-space-5, 20px);
                    right: var(--theme-space-5, 20px);
                    background-color: var(--theme-background-elevated, var(--theme-background-primary));
                    color: var(--theme-text-primary);
                    padding: var(--theme-space-3, 12px) var(--theme-space-5, 20px);
                    border-radius: var(--theme-radius-md, 8px);
                    opacity: 0;
                    transition: opacity var(--transition-speed-normal, 0.25s);
                    box-shadow: var(--theme-shadow-md);
                    border: 1px solid var(--theme-border-primary);
                    border-left: var(--theme-space-1, 4px) solid transparent;
                    pointer-events: auto;
                    max-width: 300px;
                    font-family: var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
                    font-size: var(--theme-font-size-sm, 14px);
                    z-index: 10000;
                  }
                  .toast.visible {
                    opacity: 1;
                  }
                  .toast.info { border-left-color: var(--theme-info); }
                  .toast.success { border-left-color: var(--theme-success); }
                  .toast.warning { border-left-color: var(--theme-warning); }
                  .toast.error { border-left-color: var(--theme-error); }
                `;
                
                this._container.className = 'toast';
              }
              
              static get observedAttributes(): string[] {
                return ['message', 'type', 'duration'];
              }
              
              attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
                if (oldValue === newValue) return;
                
                switch (name) {
                  case 'message':
                    this._message = newValue || '';
                    this._container.textContent = this._message;
                    break;
                  case 'type':
                    this._type = newValue || 'info';
                    this._container.className = `toast ${this._type}`;
                    break;
                  case 'duration':
                    this._duration = parseInt(newValue, 10) || 3000;
                    break;
                }
              }
              
              show(): void {
                this._container.textContent = this._message;
                this._container.className = `toast ${this._type}`;
                
                // Force repaint before changing opacity
                this._container.getBoundingClientRect();
                
                // Make toast visible
                this._container.classList.add('visible');
                
                // Set timer to hide
                setTimeout(() => {
                  this._container.classList.remove('visible');
                }, this._duration);
              }
            }
            
            // Define the SimpleToast component
            if (!customElements.get('qds-simple-toast')) {
              customElements.define('qds-simple-toast', SimpleToast);
            }
          }
          
          // Create private toast manager data
          let toastElement: HTMLElement | null = null;
          
          // Create the global toast manager that matches our shared interface
          window.QdsToastManager = {
            show(message: string, options: any = {}): HTMLElement {
              // Create the toast element if it doesn't exist
              if (!toastElement) {
                toastElement = document.createElement('qds-simple-toast');
                document.body.appendChild(toastElement);
              }
              
              // Configure the toast
              toastElement.setAttribute('message', message);
              toastElement.setAttribute('type', options.type || 'info');
              if (options.duration) {
                toastElement.setAttribute('duration', options.duration.toString());
              }
              
              // Show the toast
              (toastElement as any).show();
              return toastElement;
            },
            
            info(message: string, options: any = {}): HTMLElement {
              return this.show(message, { ...options, type: 'info' });
            },
            
            success(message: string, options: any = {}): HTMLElement {
              return this.show(message, { ...options, type: 'success' });
            },
            
            warning(message: string, options: any = {}): HTMLElement {
              return this.show(message, { ...options, type: 'warning' });
            },
            
            error(message: string, options: any = {}): HTMLElement {
              return this.show(message, { ...options, type: 'error' });
            }
          };
        }
        
        // If we still don't have a QdsToastManager (should never happen with our fallback)
        if (!window.QdsToastManager) {
          // Create a simple object with the same interface
          window.QdsToastManager = {
            show: (message: string, options: any = {}): HTMLElement => { 
              console.log(`${options.type || 'info'} Toast:`, message); 
              return document.createElement('div');
            },
            info: (message: string, options: any = {}): HTMLElement => { 
              console.log('Info Toast:', message); 
              return document.createElement('div');
            },
            success: (message: string, options: any = {}): HTMLElement => { 
              console.log('Success Toast:', message); 
              return document.createElement('div');
            },
            warning: (message: string, options: any = {}): HTMLElement => { 
              console.log('Warning Toast:', message); 
              return document.createElement('div');
            },
            error: (message: string, options: any = {}): HTMLElement => { 
              console.log('Error Toast:', message); 
              return document.createElement('div');
            }
          };
        }
        
        // Mark initialization as complete
        globalToastInitialized = true;
        globalToastInitializing = false;
        return true;
      } catch (err) {
        // In case of any error, provide a fallback and mark as initialized
        window.QdsToastManager = {
          show: (message: string, options: any = {}): HTMLElement => { 
            console.log(`${options.type || 'info'} Toast:`, message); 
            return document.createElement('div');
          },
          info: (message: string, options: any = {}): HTMLElement => { 
            console.log('Info Toast:', message); 
            return document.createElement('div');
          },
          success: (message: string, options: any = {}): HTMLElement => { 
            console.log('Success Toast:', message); 
            return document.createElement('div');
          },
          warning: (message: string, options: any = {}): HTMLElement => { 
            console.log('Warning Toast:', message); 
            return document.createElement('div');
          },
          error: (message: string, options: any = {}): HTMLElement => { 
            console.log('Error Toast:', message); 
            return document.createElement('div');
          }
        };
        
        globalToastInitialized = true;
        globalToastInitializing = false;
        return false;
      }
    })();
    
    return globalToastInitPromise;
  }
  
  /**
   * Ensures toast is available before using it
   * @private
   */
  private async _ensureToastAvailable(): Promise<boolean> {
    // If already initialized, return immediately
    if (globalToastInitialized) return true;
    
    // If initializing, wait for it to complete
    if (globalToastInitializing) {
      return globalToastInitPromise!;
    }
    
    // Otherwise, start initialization
    return this._initializeGlobalToast();
  }
  
  static get observedAttributes(): string[] {
    return [
      'color',
      'variable-name',
      'name',
      'description'
    ];
  }
  
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;
    
    switch (name) {
      case 'color':
        this._color = newValue || DEFAULT_COLOR;
        this._updateSwatch();
        break;
      case 'variable-name':
        this._variableName = newValue || '';
        this._updateInfo();
        break;
      case 'name':
        this._name = newValue || 'Color';
        this._updateInfo();
        break;
      case 'description':
        this._description = newValue || 'Color description';
        this._updateInfo();
        break;
    }
  }
  
  private _updateSwatch(): void {
    const swatch = this.shadowRoot?.querySelector('.color-swatch') as HTMLElement | null;
    if (swatch) {
      swatch.style.backgroundColor = this._color;
    }
  }
  
  private _updateInfo(): void {
    const name = this.shadowRoot?.querySelector('.color-name') as HTMLElement | null;
    const variableName = this.shadowRoot?.querySelector('.variable-name') as HTMLElement | null;
    const description = this.shadowRoot?.querySelector('.color-description') as HTMLElement | null;
    
    if (name) name.textContent = this._name;
    
    if (variableName) {
      if (this._variableName) {
        variableName.textContent = this._variableName;
        variableName.style.display = 'block';
      } else {
        variableName.style.display = 'none';
      }
    }
    
    if (description) description.textContent = this._description;
  }
  
  /**
   * Converts the current color to a hex string
   * @returns The color in hex format (#RRGGBB)
   */
  private _getHexValue(): string {
    // If the color is already a hex value, return it
    if (this._color.startsWith('#')) {
      return this._color;
    }
    
    // For other color formats, create a temporary element to compute the RGB value
    const temp = document.createElement('div');
    temp.style.color = this._color;
    document.body.appendChild(temp);
    const computedColor = getComputedStyle(temp).color;
    document.body.removeChild(temp);
    
    // Convert RGB string to hex
    const rgbMatch = computedColor.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
      const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
      const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`;
    }
    
    // Fallback
    return this._color;
  }
  
  /**
   * Converts the current color to an RGB string
   * @returns The color in rgb format (rgb(r, g, b))
   */
  private _getRgbValue(): string {
    // For all color formats, create a temporary element to compute the RGB value
    const temp = document.createElement('div');
    temp.style.color = this._color;
    document.body.appendChild(temp);
    const computedColor = getComputedStyle(temp).color;
    document.body.removeChild(temp);
    
    return computedColor;
  }
  
  /**
   * Gets the CSS variable syntax for the color
   * @returns The color as a CSS variable (var(--variable-name))
   */
  private _getCssVarValue(): string {
    if (this._variableName) {
      return `var(--${this._variableName})`;
    }
    return this._getHexValue(); // Fallback to hex if no variable name
  }
  
  /**
   * Copy hex color value to clipboard
   * @param event - The click event
   */
  private async _copyHex(event: MouseEvent): Promise<void> {
    const hexValue = this._getHexValue();
    try {
      await navigator.clipboard.writeText(hexValue);
      await this._showToast(`Copied: ${hexValue}`, 'success', event);
      this._dispatchCopiedEvent('hex', hexValue);
    } catch (err) {
      console.error('Failed to copy hex value:', err);
      await this._showToast(`Failed to copy value`, 'error', event);
    }
  }
  
  /**
   * Copy RGB color value to clipboard
   * @param event - The click event
   */
  private async _copyRgb(event: MouseEvent): Promise<void> {
    const rgbValue = this._getRgbValue();
    try {
      await navigator.clipboard.writeText(rgbValue);
      await this._showToast(`Copied: ${rgbValue}`, 'success', event);
      this._dispatchCopiedEvent('rgb', rgbValue);
    } catch (err) {
      console.error('Failed to copy RGB value:', err);
      await this._showToast(`Failed to copy value`, 'error', event);
    }
  }
  
  /**
   * Copy CSS variable syntax to clipboard
   * @param event - The click event
   */
  private async _copyCssVar(event: MouseEvent): Promise<void> {
    const cssVarValue = this._getCssVarValue();
    try {
      await navigator.clipboard.writeText(cssVarValue);
      await this._showToast(`Copied: ${cssVarValue}`, 'success', event);
      this._dispatchCopiedEvent('css-var', cssVarValue);
    } catch (err) {
      console.error('Failed to copy CSS variable value:', err);
      await this._showToast(`Failed to copy value`, 'error', event);
    }
  }
  
  /**
   * Display a toast notification message
   * @param message The message to display
   * @param type The type of toast (info, success, warning, error)
   * @param event The mouse event that triggered the toast (for cursor positioning)
   */
  private async _showToast(message: string, type: ToastType = 'info', event: MouseEvent | null = null): Promise<void> {
    try {
      // First try to use the global toast manager
      await this._ensureToastAvailable();
      
      if (window.QdsToastManager) {
        const options: any = { duration: 3000 };
        
        if (event) {
          options.position = 'cursor';
          options.event = event;
          options.cursorOffset = 15;
        }
        
        window.QdsToastManager[type](message, options);
        return;
      }
    } catch (err) {
      console.error("Error with QdsToastManager:", err);
    }
    
    // Fallback implementation if QdsToastManager is not available
    const styleId = 'color-helper-toast-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .color-helper-toast {
          position: fixed;
          z-index: 9999;
          top: var(--theme-space-5, 20px);
          right: var(--theme-space-5, 20px); 
          background-color: var(--theme-background-elevated, var(--theme-background-primary));
          color: var(--theme-text-primary);
          padding: var(--theme-space-3, 12px) var(--theme-space-5, 20px);
          border-radius: var(--theme-radius-md, 8px);
          box-shadow: var(--theme-shadow-md);
          border: 1px solid var(--theme-border-primary);
          border-left: var(--theme-space-1, 4px) solid transparent;
          font-family: var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
          font-size: var(--theme-font-size-sm, 14px);
          opacity: 0;
          transition:
            opacity var(--transition-speed-normal, 0.25s) ease,
            transform var(--transition-speed-normal, 0.25s) ease;
          transform: translateY(calc(var(--theme-space-5, 20px) * -1));
          pointer-events: none;
          max-width: 300px;
          text-align: center;
        }
        .color-helper-toast.info { border-left-color: var(--theme-info); }
        .color-helper-toast.success { border-left-color: var(--theme-success); }
        .color-helper-toast.warning { border-left-color: var(--theme-warning); }
        .color-helper-toast.error { border-left-color: var(--theme-error); }
        .color-helper-toast.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .color-helper-toast {
            left: var(--theme-space-5, 20px);
            right: var(--theme-space-5, 20px);
            text-align: center;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Create toast element
    const toast = document.createElement('div');
    toast.className = `color-helper-toast ${type}`;
    toast.textContent = message;
    
    // Add to body
    document.body.appendChild(toast);
    
    // Position near cursor if event is provided
    if (event) {
      const offset = 15;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Estimate dimensions
      const estimatedWidth = 300;
      const estimatedHeight = 80;
      
      // Calculate position
      let left = event.clientX + offset;
      let top = event.clientY + offset;
      
      // Keep within viewport
      if (left + estimatedWidth > viewportWidth) {
        left = event.clientX - estimatedWidth - offset;
      }
      
      if (top + estimatedHeight > viewportHeight) {
        top = event.clientY - estimatedHeight - offset;
      }
      
      // Apply position
      toast.style.top = `${Math.max(0, top)}px`;
      toast.style.left = `${Math.max(0, left)}px`;
      toast.style.right = 'auto';
    }
    
    // Force reflow
    void toast.offsetWidth;
    
    // Show toast
    setTimeout(() => {
      toast.classList.add('visible');
    }, 10);
    
    // Hide after delay
    setTimeout(() => {
      toast.classList.remove('visible');
      
      // Remove element after animation
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 3000);
  }
  
  /**
   * Dispatch a copied event
   * @param format The format that was copied (hex, rgb, css-var)
   * @param value The value that was copied
   */
  private _dispatchCopiedEvent(format: string, value: string): void {
    this.dispatchEvent(new CustomEvent('copied', {
      bubbles: true,
      composed: true,
      detail: {
        color: this._color,
        name: this._name,
        format,
        value
      }
    }));
  }
  
  render(): void {
    if (!this.shadowRoot) return;
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          --_color-helper-surface: var(--color-helper-background, var(--theme-background-primary));
          --_color-helper-muted-surface: var(--color-helper-muted-background, var(--theme-background-secondary));
          --_color-helper-border: var(--color-helper-border-color, var(--theme-border-primary));
          --_color-helper-text: var(--color-helper-text-color, var(--theme-text-primary));
          --_color-helper-subtle-text: var(--color-helper-subtle-text-color, var(--theme-text-secondary));
          --_color-helper-shadow: var(--color-helper-shadow, var(--theme-shadow-sm));
          display: block;
          font-family: var(--color-helper-font-family, var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif)));
          color: var(--_color-helper-text);
        }
        
        .color-card {
          border-radius: var(--color-helper-radius, var(--theme-radius-md, 8px));
          box-shadow: var(--_color-helper-shadow);
          background-color: var(--_color-helper-surface);
          display: flex;
          width: 100%;
          max-width: 320px;
          flex-direction: column;
          border: 1px solid var(--_color-helper-border);
          overflow: hidden;
        }
        
        .color-swatch {
          height: 80px; /* ~2cm square */
          width: 100%;
          background-color: ${this._color};
          transition: background-color 0.3s ease;
        }
        
        .info-card {
          padding: var(--theme-space-4, 16px);
          display: flex;
          flex-direction: column;
          gap: var(--theme-space-2, 8px);
        }
        
        .color-name {
          font-size: var(--theme-font-size-lg, 18px);
          font-weight: var(--theme-font-weight-semibold, 600);
          color: var(--_color-helper-text);
          margin: 0;
        }
        
        .variable-name {
          font-family: var(--theme-font-family-mono, var(--qds-mono-font-family, 'IBM Plex Mono', monospace));
          font-size: var(--theme-font-size-sm, 14px);
          color: var(--_color-helper-subtle-text);
          margin: 0;
          background-color: var(--_color-helper-muted-surface);
          padding: var(--theme-space-1, 4px) var(--theme-space-2, 8px);
          border-radius: var(--theme-radius-sm, 4px);
          display: inline-block;
          align-self: flex-start;
        }
        
        .color-description {
          font-size: var(--theme-font-size-sm, 14px);
          color: var(--_color-helper-subtle-text);
          line-height: 1.4;
          margin: 0;
        }
        
        .toolbar {
          display: flex;
          border-top: 1px solid var(--_color-helper-border);
          background-color: var(--_color-helper-muted-surface);
        }
        
        .toolbar-button {
          flex: 1;
          min-height: 44px;
          padding: var(--theme-space-2, 8px) var(--theme-space-1, 4px);
          text-align: center;
          font-family: inherit;
          font-size: var(--theme-font-size-xs, 12px);
          font-weight: var(--theme-font-weight-medium, 500);
          border: none;
          background: transparent;
          cursor: pointer;
          color: var(--_color-helper-subtle-text);
          transition:
            background-color var(--transition-speed-fast, 0.15s) ease,
            color var(--transition-speed-fast, 0.15s) ease;
        }
        
        .toolbar-button:hover {
          background-color: var(--theme-background-hover);
        }
        
        .toolbar-button:active {
          background-color: var(--theme-background-tertiary);
        }

        .toolbar-button:focus-visible {
          outline: 2px solid var(--theme-accent-primary);
          outline-offset: -2px;
          color: var(--_color-helper-text);
        }
        
        @media (max-width: 768px) {
          .color-card {
            max-width: 100%;
          }
        }
      </style>
      
      <div class="color-card" part="container">
        <div class="color-swatch" part="swatch"></div>
        <div class="info-card" part="info">
          <div class="color-name">${this._name}</div>
          <div class="variable-name" style="${this._variableName ? '' : 'display: none;'}">${this._variableName}</div>
          <div class="color-description">${this._description}</div>
        </div>
        <div class="toolbar" part="toolbar">
          <button class="toolbar-button hex-button">HEX</button>
          <button class="toolbar-button rgb-button">RGB</button>
          <button class="toolbar-button var-button">CSS VAR</button>
        </div>
      </div>
    `;
    
    // Update the swatch color
    this._updateSwatch();
    
    // Add event listeners to toolbar buttons
    const hexButton = this.shadowRoot.querySelector('.hex-button') as HTMLElement;
    const rgbButton = this.shadowRoot.querySelector('.rgb-button') as HTMLElement;
    const varButton = this.shadowRoot.querySelector('.var-button') as HTMLElement;
    
    // Store the event handlers so we can remove them later
    this._hexClickHandler = (event: MouseEvent) => this._copyHex(event);
    this._rgbClickHandler = (event: MouseEvent) => this._copyRgb(event);
    this._varClickHandler = (event: MouseEvent) => this._copyCssVar(event);
    
    if (hexButton) hexButton.addEventListener('click', this._hexClickHandler);
    if (rgbButton) rgbButton.addEventListener('click', this._rgbClickHandler);
    if (varButton) varButton.addEventListener('click', this._varClickHandler);
  }
  
  // Clean up event listeners when the element is removed from the DOM
  disconnectedCallback(): void {
    const hexButton = this.shadowRoot?.querySelector('.hex-button') as HTMLElement | null;
    const rgbButton = this.shadowRoot?.querySelector('.rgb-button') as HTMLElement | null;
    const varButton = this.shadowRoot?.querySelector('.var-button') as HTMLElement | null;
    
    if (hexButton) hexButton.removeEventListener('click', this._hexClickHandler);
    if (rgbButton) rgbButton.removeEventListener('click', this._rgbClickHandler);
    if (varButton) varButton.removeEventListener('click', this._varClickHandler);
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-color-helper')) {
  customElements.define('qds-color-helper', QdsColorHelper);
}

export { QdsColorHelper };
